import { AlarmEvent } from './alarm-event.enum';
import { AlarmEventNamespace } from './alarm-event-namespace.enum';

/**
 * Describes a device alarm and the associated i18n customers messages and actions required
 * Also indicates how the alarm is matched using the alarm name + short message combination
 *
 * See https://confluence.stratacache.com/pages/viewpage.action?pageId=69650963
 * **/
export interface IAlarmEvent {
  id: AlarmEvent;
  namespace: AlarmEventNamespace;
  name: (string | RegExp)[];
  message: string[];
  i18n: {
    defaultAction?: string;
    dmbMessage?: string;
    dmbAction?: string;
  };
}

export const AlarmEventLatchOpened: IAlarmEvent = {
  id: AlarmEvent.LatchOpened,
  namespace: AlarmEventNamespace.Enclosure,
  name: ['latchopen'],
  message: ['latch open'],
  i18n: {
    dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.LATCH_OPENED.MESSAGE_200',
    dmbAction: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.LATCH_OPENED.ACTION_200',
  },
}

export const AlarmEventLatchBypass: IAlarmEvent = {
  id: AlarmEvent.LatchBypass,
  namespace: AlarmEventNamespace.Enclosure,
  name: ['latchbypass'],
  message: ['latch open'],
  i18n: {
    dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.LATCH_BYPASS.MESSAGE_200',
    dmbAction: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.LATCH_BYPASS.ACTION_200',
  },
}

export const AlarmEventDoorOpened: IAlarmEvent = {
  id: AlarmEvent.DoorOpened,
  namespace: AlarmEventNamespace.Enclosure,
  name: ['dooropen'],
  message: ['door open'],
  i18n: {
    dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.DOOR_OPENED.MESSAGE_200',
    dmbAction: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.DOOR_OPENED.ACTION_200',
  },
}

export const AlarmEventFilterService: IAlarmEvent = {
  id: AlarmEvent.FilterService,
  namespace: AlarmEventNamespace.Enclosure,
  name: ['filterservice'],
  message: ['filter service required'],
  i18n: {
    dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.FILTER_SERVICE.MESSAGE_200',
    dmbAction: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.FILTER_SERVICE.ACTION_200',
  },
}

export const AlarmEventThermalTrip: IAlarmEvent = {
  id: AlarmEvent.ThermalTrip,
  namespace: AlarmEventNamespace.Enclosure,
  name: ['thermaltrip'],
  message: ['thermaltrip engaged'],
  i18n: {
    dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.THERMAL_TRIP.MESSAGE_200',
    dmbAction: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.THERMAL_TRIP.ACTION_200',
  },
};

export const AlarmEventFanError: IAlarmEvent = {
  id: AlarmEvent.FanError,
  namespace: AlarmEventNamespace.Enclosure,
  name: ['fans.error'],
  message: ['fan error'],
  i18n: {
    dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.FAN_ERROR.MESSAGE_200',
    dmbAction: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.FAN_ERROR.ACTION_200',
  },
};

/**
 * Alarms definitions
 * **/
export const AlarmEvents: IAlarmEvent[] = [
  {
    id: AlarmEvent.AssetsMissing,
    namespace: AlarmEventNamespace.Player,
    name: ['assets.missing'],
    message: ['missing contents'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.ASSETS_MISSING.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.ASSETS_MISSING.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.ASSETS_MISSING.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlayerProfileResetFailed,
    namespace: AlarmEventNamespace.Player,
    name: ['player.state.main'],
    message: ['resetting player profile failed'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_PROFILE_RESET_FAILED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_PROFILE_RESET_FAILED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_PROFILE_RESET_FAILED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.ScheduleUpdateException,
    namespace: AlarmEventNamespace.Player,
    name: ['player.state.main'],
    message: ['schedule update exception'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.SCHEDULE_UPDATE_EXCEPTION.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.SCHEDULE_UPDATE_EXCEPTION.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.SCHEDULE_UPDATE_EXCEPTION.ACTION_200',
    },
  },
  {
    id: AlarmEvent.MainNoPlaylist,
    namespace: AlarmEventNamespace.Player,
    name: ['player.state.main'],
    message: ['no playlist'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_NO_PLAYLIST.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_NO_PLAYLIST.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_NO_PLAYLIST.ACTION_200',
    },
  },
  {
    id: AlarmEvent.MainCannotReadPlaylist,
    namespace: AlarmEventNamespace.Player,
    name: ['player.state.main'],
    message: ['cannot read playlist'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_CANNOT_READ_PLAYLIST.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_CANNOT_READ_PLAYLIST.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_CANNOT_READ_PLAYLIST.ACTION_200',
    },
  },
  {
    id: AlarmEvent.MainPlaylistStartFailed,
    namespace: AlarmEventNamespace.Player,
    name: ['player.state.main'],
    message: ['playlist start failed'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_PLAYLIST_START_FAILED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_PLAYLIST_START_FAILED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.MAIN_PLAYLIST_START_FAILED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlaylistStartTimeout,
    namespace: AlarmEventNamespace.Player,
    name: [/^player\.state\.(sign|kiosk|data)\.(override|current|default)$/], // player.state.(sign|kiosk|data).(override|current|default)
    message: ['start timeout'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_START_TIMEOUT.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_START_TIMEOUT.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_START_TIMEOUT.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlaylistStartFailed,
    namespace: AlarmEventNamespace.Player,
    name: [/^player\.state\.(sign|kiosk|data)\.(override|current|default)$/],
    message: ['start failed'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_START_FAILED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_START_FAILED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_START_FAILED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.BlankPlaylist,
    namespace: AlarmEventNamespace.Player,
    name: [/^player\.state\.(sign|kiosk|data)\.(override|current|default)$/],
    message: ['blank playlist'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.BLANK_PLAYLIST.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.BLANK_PLAYLIST.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.BLANK_PLAYLIST.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlaylistCheckFailed,
    namespace: AlarmEventNamespace.Player,
    name: [/^player\.state\.(sign|kiosk|data)\.(override|current|default)$/],
    message: ['playlist check failed'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_CHECK_FAILED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_CHECK_FAILED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_CHECK_FAILED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlaylistMissingContents,
    namespace: AlarmEventNamespace.Player,
    name: [/^player\.state\.(sign|kiosk|data)\.(override|current|default)$/],
    message: ['missing contents'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_MISSING_CONTENTS.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_MISSING_CONTENTS.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYLIST_MISSING_CONTENTS.ACTION_200',
    },
  },
  {
    id: AlarmEvent.AdapterNotFound,
    namespace: AlarmEventNamespace.Player,
    name: ['adapter.state'],
    message: ['adapter not found'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.ADAPTER_NOT_FOUND.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.ADAPTER_NOT_FOUND.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.ADAPTER_NOT_FOUND.ACTION_200',
    },
  },
  {
    id: AlarmEvent.GeometryError,
    namespace: AlarmEventNamespace.Player,
    name: ['adapter.state'],
    message: ['geometry error'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.GEOMETRY_ERROR.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.GEOMETRY_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.GEOMETRY_ERROR.ACTION_200',
    },
  },
  {
    id: AlarmEvent.DisplayDisconnected,
    namespace: AlarmEventNamespace.Player,
    name: ['adapter.state'],
    message: ['display disconnected'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.DISPLAY_DISCONNECTED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.DISPLAY_DISCONNECTED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.DISPLAY_DISCONNECTED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.ResolutionNotSet,
    namespace: AlarmEventNamespace.Player,
    name: ['adapter.state'],
    message: ['resolution not set'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.RESOLUTION_NOT_SET.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.RESOLUTION_NOT_SET.ACTION_200',
    },
  },
  {
    id: AlarmEvent.AdapterError,
    namespace: AlarmEventNamespace.Player,
    name: ['adapter.state'],
    message: ['adapter error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.ADAPTER_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.ADAPTER_ERROR.ACTION_200',
    },
  },
  {
    id: AlarmEvent.ResolutionError,
    namespace: AlarmEventNamespace.Player,
    name: ['adapter.state'],
    message: ['resolution error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.RESOLUTION_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.RESOLUTION_ERROR.ACTION_200',
    },
  },
  {
    id: AlarmEvent.NoPlaylist,
    namespace: AlarmEventNamespace.Player,
    name: ['noplaylist'],
    message: ['no playlist'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.NO_PLAYLIST.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.NO_PLAYLIST.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.NO_PLAYLIST.ACTION_200',
    },
  },
  {
    id: AlarmEvent.AudioNotSet,
    namespace: AlarmEventNamespace.Player,
    name: [/^prop\.(mute|volume)$/], // prop.(mute|volume)
    message: ['not set'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.AUDIO_NOT_SET.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.AudioDisabled,
    namespace: AlarmEventNamespace.Player,
    name: [/^prop\.(mute|volume)$/],
    message: ['audio disabled'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.AUDIO_DISABLED.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.AudioQueryFailed,
    namespace: AlarmEventNamespace.Player,
    name: [/^prop\.(mute|volume)$/],
    message: ['query failed'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.AUDIO_QUERY_FAILED.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.AudioPropertyNotSupported,
    namespace: AlarmEventNamespace.Player,
    name: [/^prop\.(mute|volume)$/],
    message: ['not supported'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.AUDIO_PROPERTY_NOT_SUPPORTED.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.WrongAudioPropertyValue,
    namespace: AlarmEventNamespace.Player,
    name: [/^prop\.(mute|volume)$/],
    message: ['wrong (manual|override|auto) value'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.WRONG_AUDIO_PROPERTY_VALUE.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.WRONG_AUDIO_PROPERTY_VALUE.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.BlankScreenDetected,
    namespace: AlarmEventNamespace.Player,
    name: [/^prop\.(mute|volume)$/],
    message: ['blank screen detected'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.BLANK_SCREEN_DETECTED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.BLANK_SCREEN_DETECTED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.NoPlaybackStatus,
    namespace: AlarmEventNamespace.Player,
    name: [/^playback\.\d+\.channel$/], // playback.<C>.channel
    message: ['no status'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.NO_PLAYBACK_STATUS.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.NO_PLAYBACK_STATUS.ACTION_200',
    },
  },
  {
    id: AlarmEvent.RegionPlaybackIssue,
    namespace: AlarmEventNamespace.Player,
    name: [/^playback\.\d+\.region\..+$/], // playback.<C>.region.<R>
    message: ['command input timeout', 'failed to launch GPU process', 'GL Problem', 'browser has crashed', 'cannot allocate memory'], // TODO (also SmileBackEnd errors, see confluence)
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.REGION_PLAYBACK_ISSUE.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.REGION_PLAYBACK_ISSUE.ACTION_200',
    },
  },
  {
    id: AlarmEvent.WrongRegionState,
    namespace: AlarmEventNamespace.Player,
    name: [/^playback\.\d+\.region\..+$/],
    message: ['wrong region state'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.WRONG_REGION_STATE.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.WRONG_REGION_STATE.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlayerStopped,
    namespace: AlarmEventNamespace.Player,
    name: [/^playback\.\d+$/], //  playback.<C>
    message: ['player stopped'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_STOPPED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_STOPPED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PlayerOverrideDetected,
    namespace: AlarmEventNamespace.Player,
    name: ['state'],
    message: ['manual override'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_OVERRIDE_DETECTED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_OVERRIDE_DETECTED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.PLAYER.PLAYER_OVERRIDE_DETECTED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.ConnectionFailed,
    namespace: AlarmEventNamespace.Display,
    name: [/^prop\.(power|input|mute|volume)$/], // prop.(power|input|mute|volume)
    message: ['connection failed'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.DISPLAY.CONNECTION_FAILED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.DISPLAY.CONNECTION_FAILED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PropertyNotSet,
    namespace: AlarmEventNamespace.Display,
    name: ['prop.(power|input|mute|volume)'],
    message: ['not set'],
    i18n: {},
  },
  {
    id: AlarmEvent.AdapterOff,
    namespace: AlarmEventNamespace.Display,
    name: ['prop.(power|input|mute|volume)'],
    message: ['adapter off'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.DISPLAY.ADAPTER_OFF.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.DISPLAY.ADAPTER_OFF.ACTION_200',
    },
  },
  {
    id: AlarmEvent.WrongPropertyValue,
    namespace: AlarmEventNamespace.Display,
    name: ['prop.(power|input|mute|volume)'],
    message: ['wrong (manual|override|auto) value'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.DISPLAY.WRONG_PROPERTY_VALUE.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.DISPLAY.WRONG_PROPERTY_VALUE.ACTION_200',
      defaultAction: 'alarmEvents.ALARM_EVENTS.DISPLAY.WRONG_PROPERTY_VALUE.DEFAULT_ACTION_200',
    },
  },
  {
    id: AlarmEvent.MaintenanceInProgress,
    namespace: AlarmEventNamespace.Scheduler,
    name: ['interface'],
    message: ['operation in progress'],
    i18n: {},
  },
  {
    id: AlarmEvent.BackupConfigurationInUse,
    namespace: AlarmEventNamespace.Scheduler,
    name: ['interface'],
    message: ['backup configuration in use'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.SCHEDULER.BACKUP_CONFIGURATION_IN_USE.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.SCHEDULER.BACKUP_CONFIGURATION_IN_USE.ACTION_200',
    },
  },
  {
    id: AlarmEvent.SchedulerAssetsMissing,
    namespace: AlarmEventNamespace.Cms,
    name: ['assets'],
    message: ['assets missing'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.CMS.SCHEDULER_ASSETS_MISSING.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.CMS.SCHEDULER_ASSETS_MISSING.ACTION_200',
    },
  },
  {
    id: AlarmEvent.RebootCycleDetected,
    namespace: AlarmEventNamespace.System,
    name: ['boot'],
    message: ['reboot cycle'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.SYSTEM.REBOOT_CYCLE_DETECTED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.SYSTEM.REBOOT_CYCLE_DETECTED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.NtpError,
    namespace: AlarmEventNamespace.Ntp,
    name: ['state'],
    message: ['NTP service is down', 'no NTP server found', 'NTP not synchronized', 'NTP still synchronizing'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.NTP.NTP_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.NTP.NTP_ERROR.ACTION_200',
      defaultAction: 'alarmEvents.ALARM_EVENTS.NTP.NTP_ERROR.DEFAULT_ACTION_200',
    },
  },
  {
    id: AlarmEvent.RebootNeeded,
    namespace: AlarmEventNamespace.System,
    name: ['run.needreboot'],
    message: ['reboot needed'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.SYSTEM.REBOOT_NEEDED.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.ControllerFailed,
    namespace: AlarmEventNamespace.Enclosure,
    name: ['mcstate'],
    message: ['controller error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.CONTROLLER_FAILED.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.KeyDisabled,
    namespace: AlarmEventNamespace.Enclosure,
    name: ['keyen'],
    message: ['key disabled'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.KEY_DISABLED.MESSAGE_200',
    },
  },
  AlarmEventLatchOpened,
  AlarmEventLatchBypass,
  AlarmEventDoorOpened,
  AlarmEventFilterService,
  AlarmEventThermalTrip,
  AlarmEventFanError,
  {
    id: AlarmEvent.TemperatureSensorError,
    namespace: AlarmEventNamespace.Enclosure,
    name: ['tempsensors'],
    message: ['temp sensor error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.TEMPERATURE_SENSOR_ERROR.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.PowerMissing,
    namespace: AlarmEventNamespace.Enclosure,
    name: ['valarm'],
    message: ['48V no power'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.POWER_MISSING.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.DeviceError,
    namespace: AlarmEventNamespace.Enclosure,
    name: ['valardevice.errorm'],
    message: ['device error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.ENCLOSURE.DEVICE_ERROR.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.RebootInProgress,
    namespace: AlarmEventNamespace.Enclosure,
    name: ['reboot'],
    message: ['enclosure reboot'],
    i18n: {},
  },
  {
    id: AlarmEvent.TemperatureTooLow,
    namespace: AlarmEventNamespace.Temperature,
    name: ['system.low', 'enclosure.low', /^display\d+.low$/],
    message: ['abnormal temperature'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.TEMPERATURE.TEMPERATURE_TOO_LOW.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.TemperatureTooHigh,
    namespace: AlarmEventNamespace.Temperature,
    name: ['system.high', 'enclosure.high', /^display\d+.high$/],
    message: ['abnormal temperature'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.TEMPERATURE.TEMPERATURE_TOO_HIGH.MESSAGE_200',
    },
  },
  {
    id: AlarmEvent.PosUncaughtException,
    namespace: AlarmEventNamespace.POS,
    name: ['app'],
    message: ['uncaught exception'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_UNCAUGHT_EXCEPTION.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_UNCAUGHT_EXCEPTION.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PosDatabaseError,
    namespace: AlarmEventNamespace.POS,
    name: ['database'],
    message: ['database error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_DATABASE_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_DATABASE_ERROR.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PosDataParsingError,
    namespace: AlarmEventNamespace.POS,
    name: ['data'],
    message: ['parsing error', 'data error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_DATA_PARSING_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_DATA_PARSING_ERROR.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PosMisconfiguration,
    namespace: AlarmEventNamespace.POS,
    name: ['conf'],
    message: ['failed pos module', 'failed conf', 'transfer mode mismatch', 'configured for poll', 'received post'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.POS.POS_MISCONFIGURATION.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_MISCONFIGURATION.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_MISCONFIGURATION.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PosPermissionDenied,
    namespace: AlarmEventNamespace.POS,
    name: ['client'],
    message: ['permission denied', 'data send failure'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.POS.POS_PERMISSION_DENIED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_PERMISSION_DENIED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_PERMISSION_DENIED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PosDataFetchFailed,
    namespace: AlarmEventNamespace.POS,
    name: ['data'],
    message: ['data fetch failure'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.POS.POS_DATA_FETCH_FAILED.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_DATA_FETCH_FAILED.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_DATA_FETCH_FAILED.ACTION_200',
    },
  },
  {
    id: AlarmEvent.PosStaleUpdates,
    namespace: AlarmEventNamespace.POS,
    name: ['client'],
    message: ['stale pos updates'],
    i18n: {
      defaultAction: 'alarmEvents.ALARM_EVENTS.POS.POS_STALE_UPDATES.DEFAULT_ACTION_200',
      dmbMessage: 'alarmEvents.ALARM_EVENTS.POS.POS_STALE_UPDATES.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.POS.POS_STALE_UPDATES.ACTION_200',
    },
  },
  {
    id: AlarmEvent.CodError,
    namespace: AlarmEventNamespace.COD,
    name: ['app'],
    message: ['Parsing Error'],
    i18n: {
      dmbMessage: 'alarmEvents.ALARM_EVENTS.COD.COD_ERROR.MESSAGE_200',
      dmbAction: 'alarmEvents.ALARM_EVENTS.COD.COD_ERROR.ACTION_200',
    },
  },
];
